'use client';

import { createContext, useContext, type ReactNode } from 'react';
import { useWebphone } from './use-webphone';

type WebphoneContextValue = ReturnType<typeof useWebphone>;

const WebphoneContext = createContext<WebphoneContextValue | null>(null);

/**
 * Instancia UMA única vez o useWebphone() (um registro SIP por app) e
 * compartilha via contexto. Montado no dashboard layout quando a voz está
 * habilitada. Antes, cada componente que chamasse useWebphone() criaria um
 * Registerer próprio — o provider evita isso e deixa a página do supervisor
 * ler o estado de registro sem registrar de novo.
 */
export function WebphoneProvider({ children }: { children: ReactNode }) {
  const wp = useWebphone();
  return <WebphoneContext.Provider value={wp}>{children}</WebphoneContext.Provider>;
}

/** Acesso ao webphone compartilhado. Lança fora do provider. */
export function useWebphoneContext(): WebphoneContextValue {
  const ctx = useContext(WebphoneContext);
  if (!ctx) {
    throw new Error('useWebphoneContext deve ser usado dentro de <WebphoneProvider>');
  }
  return ctx;
}

/**
 * Seletor leve pra componentes que só precisam saber se o webphone está
 * registrado e em qual ramal (ex: gating do botão Puxar). Tolera ausência de
 * provider (voz desligada) retornando não-registrado.
 */
export function useWebphoneRegistration(): {
  isRegistered: boolean;
  extension: string | null;
} {
  const ctx = useContext(WebphoneContext);
  if (!ctx) return { isRegistered: false, extension: null };
  return { isRegistered: ctx.state === 'registered', extension: ctx.extension };
}
